import type { ComponentProps, ReactElement } from "react";

import { Builder, type CompositionComponent } from "./builder";

export class BaseSingleBuilder<
  C extends readonly [CompositionComponent, ...CompositionComponent[]],
> extends Builder<C, ReactElement<ComponentProps<C[number]>, C[number]>> {
  public type = "single" as const;
}

export class RequiredSingleBuilder<
  C extends readonly [CompositionComponent, ...CompositionComponent[]],
> extends BaseSingleBuilder<C> {
  public modifier = "required" as const;
  constructor(components: C) {
    super(components);
    this._required = true;
  }
}

export class DefaultedSingleBuilder<
  C extends readonly [CompositionComponent, ...CompositionComponent[]],
> extends BaseSingleBuilder<C> {
  public modifier = "defaulted" as const;
  constructor(
    components: C,
    defaultElement: ReactElement<ComponentProps<C[number]>, C[number]>,
  ) {
    super(components);
    this._default = defaultElement;
  }
}

export class SingleBuilder<
  C extends readonly [CompositionComponent, ...CompositionComponent[]],
> extends BaseSingleBuilder<C> {
  required(): RequiredSingleBuilder<C> {
    const required = new RequiredSingleBuilder<C>(this.components);
    for (const alias of this._aliases) {
      required.alias(alias);
    }
    return required;
  }

  default(
    value: ReactElement<ComponentProps<C[number]>, C[number]>,
  ): DefaultedSingleBuilder<C> {
    const defaulted = new DefaultedSingleBuilder<C>(this.components, value);
    for (const alias of this._aliases) {
      defaulted.alias(alias);
    }
    return defaulted;
  }
}
